import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import { ErrorOutline, MailOutline, SearchOff } from '@mui/icons-material';
import { appContext } from '@utils';
import { CustomButton } from '@components';
import { COLORS } from '@styles';
import { ClientDrawer } from '../Organizations/_/ClientDrawer/ClientDrawer';
import { EmailLookupInput } from './_/EmailLookupInput/EmailLookupInput';
import { LookupResultCard } from './_/LookupResultCard/LookupResultCard';

const makeStyles = () => ({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
    padding: '2rem 3rem',
    color: COLORS.NEUTRAL_WHITE,
  } as React.CSSProperties,
  titleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  subtitle: {
    color: COLORS.NEUTRAL_LIGHT_0,
  },
  form: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '1rem',
    flexWrap: 'wrap',
  } as React.CSSProperties,
  helperText: {
    color: COLORS.NEUTRAL_LIGHT_0,
    marginTop: '-1rem',
  },
  emptyPanel: {
    border: `1px solid ${COLORS.NEUTRAL_DARK_1}`,
    borderRadius: '0.5rem',
    padding: '4rem 2rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.75rem',
    textAlign: 'center',
  } as React.CSSProperties,
  emptyPanelIcon: {
    color: COLORS.NEUTRAL_LIGHT_0,
    fontSize: '2.5rem',
  },
  emptyPanelSecondary: {
    color: COLORS.NEUTRAL_LIGHT_0,
    maxWidth: '32rem',
  },
  activityNote: {
    color: COLORS.NEUTRAL_LIGHT_0,
    marginTop: '-1rem',
  },
});

export const UsersPage = observer(() => {
  const sx = makeStyles();
  const {
    usersStore: { lookupUserByEmail, lookupResult, lookupResponse, resetLookup },
  } = useContext(appContext);

  const [email, setEmail] = useState('');
  const [searchedEmail, setSearchedEmail] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => () => resetLookup(), [resetLookup]);

  const runLookup = async (lookupEmail: string) => {
    await lookupUserByEmail(lookupEmail);
    setSearchedEmail(lookupEmail);
    setHasSearched(true);
  };

  const lookupFailed = lookupResponse.status === 'badRequest';
  const lookupSucceeded = lookupResponse.status === 'ok';

  // The edit may have changed the address that was searched, so look the user up again by the
  // saved value of whichever field matched; searching the old address would show "No user found".
  const handleSaved = (saved: { email?: string; workEmail?: string }) => {
    const normalizedSearch = searchedEmail.trim().toLowerCase();
    const matchedWorkOnly =
      lookupResult?.email?.trim().toLowerCase() !== normalizedSearch &&
      lookupResult?.workEmail?.trim().toLowerCase() === normalizedSearch;
    const nextEmail = (matchedWorkOnly ? saved?.workEmail : saved?.email) || searchedEmail;
    setEmail(nextEmail);
    runLookup(nextEmail);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    runLookup(email);
  };

  return (
    <Box sx={sx.pageContainer}>
      <Box sx={sx.titleBlock}>
        <Typography variant="h5">Users</Typography>
        <Typography sx={sx.subtitle}>
          Find a user in any organization by their exact sign-in or work email.
        </Typography>
      </Box>

      <form style={sx.form} onSubmit={handleSubmit}>
        <EmailLookupInput value={email} onChange={setEmail} />
        <CustomButton
          variant="default"
          width="auto"
          type="submit"
          isLoading={lookupResponse.status === 'pending'}
        >
          Look up
        </CustomButton>
      </form>
      <Typography variant="body2" sx={sx.helperText}>
        Exact match only. Capitalization and spaces at the ends are ignored.
      </Typography>

      {!hasSearched && (
        <Box sx={sx.emptyPanel}>
          <MailOutline sx={sx.emptyPanelIcon} />
          <Typography variant="subtitle1" fontWeight={600}>
            Enter an email address to look up a user
          </Typography>
          <Typography variant="body2" sx={sx.emptyPanelSecondary}>
            You will see their organization, work email and full user record. Every lookup is
            recorded in the activity log.
          </Typography>
        </Box>
      )}

      {hasSearched && lookupFailed && (
        <Box sx={sx.emptyPanel}>
          <ErrorOutline sx={sx.emptyPanelIcon} />
          <Typography variant="subtitle1" fontWeight={600}>
            The lookup could not be completed
          </Typography>
          <Typography variant="body2" sx={sx.emptyPanelSecondary}>
            Something went wrong while searching. Try again in a moment.
          </Typography>
        </Box>
      )}

      {hasSearched && lookupSucceeded && !lookupResult && (
        <>
          <Box sx={sx.emptyPanel}>
            <SearchOff sx={sx.emptyPanelIcon} />
            <Typography variant="subtitle1" fontWeight={600}>
              No user found with that email
            </Typography>
            <Typography variant="body2" sx={sx.emptyPanelSecondary}>
              Check the full address for typos. Partial addresses and domains are not searched.
            </Typography>
          </Box>
          <Typography variant="body2" sx={sx.activityNote}>
            This lookup was recorded in the activity log.
          </Typography>
        </>
      )}

      {hasSearched && lookupResult && (
        <>
          <LookupResultCard
            client={lookupResult}
            searchedEmail={searchedEmail}
            onEdit={() => setEditOpen(true)}
          />
          <Typography variant="body2" sx={sx.activityNote}>
            This lookup was recorded in the activity log.
          </Typography>
        </>
      )}

      <ClientDrawer
        open={editOpen}
        onClose={() => setEditOpen(false)}
        client={lookupResult}
        isEdit
        onSaved={handleSaved}
      />
    </Box>
  );
});
